import React from "react";

const ProjectInikaPrivacyPolicy = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8 text-gray-200">
      <h1 className="text-3xl font-bold mb-4 text-white">PRIVACY POLICY</h1>
      <h2 className="text-2xl font-semibold mb-6 text-gray-300">
        Project Inika QuickBooks Implementation
      </h2>

      <p className="mb-4 text-gray-400">
        <strong>Last Updated: February 20, 2025</strong>
      </p>

      <p className="mb-4">
        Project Inika ("we," "our," or "us") is committed to protecting your
        privacy. This Privacy Policy explains how we collect, use, disclose, and
        safeguard your information when you use our QuickBooks implementation
        and related services (collectively, the "Service").
      </p>

      <p className="mb-4">
        Please read this Privacy Policy carefully. By accessing or using the
        Service, you acknowledge that you have read, understood, and agree to be
        bound by this Privacy Policy. If you do not agree with our policies and
        practices, please do not use our Service.
      </p>

      <div className="space-y-8">
        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            1. INFORMATION WE COLLECT
          </h3>
          <p className="mb-4">
            We may collect the following types of information:
          </p>

          <div className="ml-4 mb-6">
            <h4 className="text-lg font-medium mb-2 text-gray-300">
              1.1 Personal Information
            </h4>
            <ul className="list-disc list-inside space-y-2 text-gray-300">
              <li>
                <strong>Account Information:</strong> Name, email address,
                postal address, phone number, username, password, and other
                contact details.
              </li>
              <li>
                <strong>Business Information:</strong> Company name, business
                type, industry, employer identification number, and other
                business-related information.
              </li>
              <li>
                <strong>Financial Information:</strong> Bank account
                information, credit card details, transaction history,
                accounting data, payroll information, tax information, and other
                financial records.
              </li>
              <li>
                <strong>Employee Information:</strong> If you use our payroll
                features, we may collect information about your employees,
                including names, addresses, social security numbers, salary
                information, and tax withholding data.
              </li>
            </ul>
          </div>
        </div>

        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            2. HOW WE COLLECT INFORMATION
          </h3>
          <p className="mb-4">We collect information in the following ways:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>
              <strong>Directly from You:</strong> Information you provide when
              you register for the Service, create or modify your account, set
              up preferences, or communicate with us.
            </li>
            <li>
              <strong>Automatically:</strong> Information collected
              automatically through cookies and similar technologies when you
              use the Service.
            </li>
            <li>
              <strong>From Third Parties:</strong> We may receive information
              about you from third parties, including business partners,
              identity verification services, credit bureaus, and data
              providers.
            </li>
          </ul>
        </div>

        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            3. HOW WE USE YOUR INFORMATION
          </h3>
          <p className="mb-4">
            We may use the information we collect for various purposes,
            including to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Provide, maintain, and improve the Service</li>
            <li>Process transactions and send related information</li>
            <li>Create and maintain your account</li>
            <li>
              Send administrative information, such as updates, security alerts,
              and support messages
            </li>
            <li>Respond to your comments, questions, and requests</li>
            <li>Provide customer support</li>
            <li>
              Communicate with you about products, services, offers, and events
            </li>
            <li>Monitor and analyze trends, usage, and activities</li>
            <li>
              Detect, prevent, and address technical issues, fraud, and other
              illegal activities
            </li>
            <li>Comply with legal obligations</li>
            <li>Enforce our terms, conditions, and policies</li>
          </ul>
        </div>

        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            4. DISCLOSURE OF YOUR INFORMATION
          </h3>
          <p className="mb-4">
            We may disclose your information in the following circumstances:
          </p>

          <div className="ml-4 mb-6">
            <h4 className="text-lg font-medium mb-2 text-gray-300">
              4.1 Service Providers
            </h4>
            <p className="mb-4">
              We may share your information with third-party vendors, service
              providers, contractors, or agents who perform functions on our
              behalf, such as hosting, data analysis, payment processing,
              customer service, and marketing assistance.
            </p>
          </div>

          <div className="ml-4 mb-6">
            <h4 className="text-lg font-medium mb-2 text-gray-300">
              4.2 Business Transfers
            </h4>
            <p className="mb-4">
              If we are involved in a merger, acquisition, financing, or sale of
              all or a portion of our assets, your information may be
              transferred as part of that transaction.
            </p>
          </div>

          <div className="ml-4 mb-6">
            <h4 className="text-lg font-medium mb-2 text-gray-300">
              4.3 Legal Requirements
            </h4>
            <p className="mb-4">
              We may disclose your information if required to do so by law or in
              response to valid requests by public authorities (e.g., a court or
              government agency).
            </p>
          </div>

          <div className="ml-4 mb-6">
            <h4 className="text-lg font-medium mb-2 text-gray-300">
              4.4 Protection of Rights
            </h4>
            <p className="mb-4">
              We may disclose your information to protect the rights, property,
              or safety of Project Inika, our users, or others.
            </p>
          </div>

          <div className="ml-4 mb-6">
            <h4 className="text-lg font-medium mb-2 text-gray-300">
              4.5 With Your Consent
            </h4>
            <p className="mb-4">
              We may disclose your information with your consent.
            </p>
          </div>
        </div>

        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            5. DATA SECURITY
          </h3>
          <p className="mb-4">
            We implement appropriate technical and organizational measures to
            protect the security of your personal information. However, please
            be aware that no method of transmission over the Internet or method
            of electronic storage is 100% secure. While we strive to use
            commercially acceptable means to protect your information, we cannot
            guarantee its absolute security.
          </p>
        </div>

        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            6. DATA RETENTION
          </h3>
          <p className="mb-4">
            We will retain your information for as long as your account is
            active, as necessary to provide you with the Service, or as needed
            to comply with our legal obligations, resolve disputes, and enforce
            our agreements. When we no longer need to use your information, we
            will remove it from our systems or anonymize it so that it can no
            longer be associated with you.
          </p>
        </div>

        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            7. YOUR RIGHTS AND CHOICES
          </h3>
          <p className="mb-4">
            Depending on your location, you may have certain rights regarding
            your personal information, which may include:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>
              <strong>Access:</strong> The right to access the personal
              information we hold about you.
            </li>
            <li>
              <strong>Correction:</strong> The right to request correction of
              inaccurate or incomplete information.
            </li>
            <li>
              <strong>Deletion:</strong> The right to request deletion of your
              personal information.
            </li>
            <li>
              <strong>Restriction:</strong> The right to request restriction of
              processing of your personal information.
            </li>
            <li>
              <strong>Data Portability:</strong> The right to receive your
              personal information in a structured, commonly used, and
              machine-readable format.
            </li>
            <li>
              <strong>Objection:</strong> The right to object to processing of
              your personal information.
            </li>
            <li>
              <strong>Withdrawal of Consent:</strong> The right to withdraw
              consent at any time where we rely on consent to process your
              personal information.
            </li>
          </ul>
          <p className="mb-4">
            To exercise these rights, please contact us using the information
            provided in the "Contact Us" section below.
          </p>
        </div>

        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            8. CHILDREN'S PRIVACY
          </h3>
          <p className="mb-4">
            The Service is not intended for children under the age of 18. We do
            not knowingly collect personal information from children under 18.
            If you are a parent or guardian and believe your child has provided
            us with personal information, please contact us.
          </p>
        </div>

        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            9. THIRD-PARTY LINKS AND SERVICES
          </h3>
          <p className="mb-4">
            The Service may contain links to third-party websites, services, or
            applications. We are not responsible for the privacy practices or
            content of these third parties. We encourage you to read the privacy
            policies of any third-party websites or services you visit.
          </p>
        </div>

        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            10. INTERNATIONAL DATA TRANSFERS
          </h3>
          <p className="mb-4">
            Your information may be transferred to and processed in countries
            other than your country of residence. These countries may have data
            protection laws that differ from the laws of your country. By using
            the Service, you consent to the transfer of your information to
            countries outside your country of residence, including the United
            States.
          </p>
        </div>

        <div className="section">
          <h3 className="text-xl font-semibold mb-4 text-white">
            11. CHANGES TO THIS PRIVACY POLICY
          </h3>
          <p className="mb-4">
            We reserve the right to update or change this Privacy Policy at any
            time. If we make material changes to this Privacy Policy, we will
            notify you by email or by posting a notice on our website prior to
            the changes becoming effective. Your continued use of the Service
            after we post any modifications to the Privacy Policy will
            constitute your acknowledgment of the modifications and your consent
            to be bound by the modified Privacy Policy.
          </p>
        </div>
      </div>

      <div className="mt-8 border-t border-gray-700 pt-8">
        <h3 className="text-xl font-semibold mb-4 text-white">
          12. CONTACT US
        </h3>
        <p className="mb-4">
          If you have any questions about this Privacy Policy, please contact us
          at:
        </p>
        <address className="text-gray-300 not-italic">
          Project Inika
          <br />
          823 Congress Ave, Austin, TX 78701, Suite for Industrious c/o Project
          Inika
          <br />
          ykothari@inika.app
        </address>
      </div>
    </div>
  );
};

export default ProjectInikaPrivacyPolicy;
